

class StackedNavigation {
    constructor() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.initialize());
        } else {
            this.initialize();
        }
        this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    initialize() {
        this.container = document.querySelector('.stacked-pages-container');
        if (!this.container) {
            console.error('Could not find .stacked-pages-container');
            return;
        }

        this.stack = [];
        this.scrollPositions = new Map();
        this.isInitialized = false;
        this.lastScrollPosition = 0;
        this.isNavigating = false;
        this.activePageIndex = 0;
        this.activePane = null;
        
        // Initialize async
        this.init();
    }

    async init() {
        await this.handleInitialStack();
        this.setupEventListeners();
    }

    setupEventListeners() {
        // Intercept internal links in container
        this.container.addEventListener('click', async (e) => {
            // Check if mobile screen (width less than 768px)
            if (window.innerWidth < 768) return;

            const link = e.target.closest('a');
            if (!link) return;

            // Check if link is internal by comparing origin
            const linkUrl = new URL(link.href);
            if (linkUrl.origin !== window.location.origin) return;

            // Check if link points to a path (not hash, tel:, mailto:, etc)
            if (!linkUrl.pathname || linkUrl.pathname === '/') return;

            // Handle Safari differently - moved before preventDefault()
            if (this.isSafari) {
                e.preventDefault();
                console.log('Safari detected, handling navigation differently');
                
                // Add check for tag pages and index pages
                const targetPath = linkUrl.pathname;
                if (targetPath.includes('/tag/') || targetPath.endsWith('-index') || targetPath.endsWith('-index/')) {
                    window.location.href = link.href;
                    return;
                }
                
                // Get current stack of pages
                const pages = Array.from(this.container.querySelectorAll('.stacked-page'));
                
                // Check if the target URL already exists in the stack
                const targetUrl = linkUrl.pathname.replace(/\/$/, '');
                const existingPageIndex = pages.findIndex(page => {
                    const pageUrl = new URL(page.dataset.url).pathname.replace(/\/$/, '');
                    return pageUrl === targetUrl;
                });

                if (existingPageIndex !== -1) {
                    // If the page exists, scroll to it with animation
                    pages[existingPageIndex].style.scrollBehavior = 'smooth';
                    pages[existingPageIndex].style.transition = 'all 0.3s ease-in-out';
                    pages[existingPageIndex].scrollIntoView({ behavior: 'smooth', inline: 'start' });
                    return;
                }
                
                // Find the origin page and its index
                const originPage = link.closest('.stacked-page');
                if (!originPage) return;
                const originIndex = pages.indexOf(originPage);
                
                // Get the current stack parameter
                const currentUrl = new URL(window.location.href);
                const currentStack = currentUrl.searchParams.get('stack');
                let stackUrls = currentStack ? 
                    currentStack.split(',').map(path => `${window.location.origin}${decodeURIComponent(path)}`) :
                    [pages[0].dataset.url];

                // Keep only the URLs up to the origin page
                stackUrls = stackUrls.slice(0, originIndex + 1);
                
                // Add the new URL
                stackUrls.push(link.href);
                
                // Create stack parameter
                const stackPaths = stackUrls.map(url => {
                    const urlObj = new URL(url);
                    return encodeURIComponent(urlObj.pathname.replace(/\/+$/, ''));
                });

                // Create new URL with updated stack
                const newUrl = new URL(link.href);
                newUrl.searchParams.set('stack', stackPaths.join(','));
                
                // Debug log
                console.log('Current stack:', stackUrls);
                console.log('Stack paths:', stackPaths);
                console.log('Redirecting to:', newUrl.toString());
                
                // Force the navigation
                window.location.href = newUrl.toString();
                return false;
            }

            e.preventDefault();

            // Normalize URLs for comparison by removing trailing slashes
            const targetUrl = linkUrl.pathname.replace(/\/$/, '');
            
            // Find the origin page and its index
            const originPage = link.closest('.stacked-page');
            if (!originPage) return;
            
            const pages = Array.from(this.container.querySelectorAll('.stacked-page'));
            
            // Check if the target URL is already in the stack
            const existingPage = pages.find(page => {
                const pageUrl = new URL(page.dataset.url).pathname.replace(/\/$/, '');
                return pageUrl === targetUrl;
            });

            if (existingPage) {
                // If the page exists, just scroll to it
                existingPage.scrollIntoView({ behavior: 'smooth', inline: 'start' });
                return;
            }

           
            
            // Continue with existing logic for new pages...
            const originIndex = pages.indexOf(originPage);
            const nextPages = pages.slice(originIndex + 1);
            const nextPage = nextPages[0];
            
            if (nextPage) {
                this.isNavigating = true;
                try {
                    // Remove all pages after the first next page
                    nextPages.slice(1).forEach(page => page.remove());
                    
                    const response = await fetch(link.href);
                    const html = await response.text();
                    
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');
                    
                    const mainContent = doc.querySelector('.site-main') || 
                                      doc.querySelector('.gh-content') ||
                                      doc.querySelector('.gh-viewport > *');
                                          
                    if (!mainContent) {
                        console.error('Could not find main content in loaded page');
                        return;
                    }

                    // Update the existing next page
                    const viewport = nextPage.querySelector('.gh-viewport');
                    if (viewport) {
                        viewport.innerHTML = '';
                        viewport.appendChild(mainContent.cloneNode(true));
                        nextPage.dataset.url = link.href;
                        
                        // Smooth scroll to the updated page
                        nextPage.scrollIntoView({ behavior: 'smooth', inline: 'start' });
                        
                        // Update history state with only the remaining pages
                        const stackUrls = pages.slice(0, originIndex + 2).map(page => page.dataset.url);
                        const stackPaths = stackUrls.map(url => {
                            const urlObj = new URL(url);
                            return encodeURIComponent(urlObj.pathname);
                        });
                        
                        const newUrl = new URL(link.href);
                        newUrl.searchParams.set('stack', stackPaths.join(','));
                        
                        window.history.pushState({
                            stack: stackUrls,
                            currentUrl: link.href
                        }, '', newUrl.toString());
                    }
                } catch (error) {
                    console.error('Navigation failed:', error);
                } finally {
                    this.isNavigating = false;
                }
            } else {
                // If there's no next page, create a new one as before
                this.navigateTo(link.href);
            }
        });

        // Handle back/forward navigation
        window.addEventListener('popstate', (e) => {
            if (e.state && e.state.stack) {
                this.handleHistoryNavigation(e.state);
            }
        });

        // Modify scroll handler - remove button visibility logic
        this.container.addEventListener('scroll', (e) => {
            if (this.isNavigating) return;

            const containerRect = this.container.getBoundingClientRect();
            const pages = Array.from(this.container.querySelectorAll('.stacked-page'));
            
            let mostVisiblePage = null;
            let maxVisibleWidth = 0;

            pages.forEach(page => {
                const rect = page.getBoundingClientRect();
                const visibleLeft = Math.max(rect.left, containerRect.left);
                const visibleRight = Math.min(rect.right, containerRect.right);
                const visibleWidth = Math.max(0, visibleRight - visibleLeft);

                if (visibleWidth > maxVisibleWidth) {
                    maxVisibleWidth = visibleWidth;
                    mostVisiblePage = page;
                }
            });
            
            if (mostVisiblePage) {
                this.activePane = mostVisiblePage;
            }

            // Update classes for all pages
            pages.forEach((page, index) => {
                if (page === mostVisiblePage) {
                    page.classList.remove('collapsed');
                    page.classList.add('active');
                } else if (pages.indexOf(page) < pages.indexOf(mostVisiblePage)) {
                    page.classList.add('collapsed');
                    page.classList.remove('active');
                } else {
                    page.classList.remove('collapsed', 'active');
                }
            });

            this.lastScrollPosition = this.container.scrollLeft;
        });

        // Add initial page without collapsed state
        const initialPage = this.container.querySelector('.stacked-page');
        if (initialPage) {
            initialPage.classList.add('current-page', 'active');
            initialPage.classList.remove('collapsed');
            initialPage.dataset.url = window.location.href;
            this.isInitialized = true;
            this.activePane = initialPage;
        }
    }

    getTransformX(element) {
        const transform = window.getComputedStyle(element).transform;
        if (transform === 'none') return 0;
        const matrix = new DOMMatrix(transform);
        return (matrix.m41 / element.offsetWidth) * 100;
    }

    async navigateTo(url) {
        this.isNavigating = true;
        
        try {
            const urlObj = new URL(url);
            // Check if URL contains /tag/ or ends with -index
            if (urlObj.pathname.includes('/tag/') || urlObj.pathname.endsWith('-index') || urlObj.pathname.endsWith('-index/')) {
                window.location.href = url;
                return;
            }
            
            const response = await fetch(url);
            const html = await response.text();
            
            // Parse the HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Find the main content
            const mainContent = doc.querySelector('.site-main') || 
                              doc.querySelector('.gh-content') ||
                              doc.querySelector('.gh-viewport > *');
                              
            if (!mainContent) {
                console.error('Could not find main content in loaded page');
                return;
            }

            this.logPageContent(mainContent, 'navigateTo');

            const newPage = document.createElement('div');
            newPage.className = 'stacked-page';
            
            // Create viewport container for consistent layout
            const viewport = document.createElement('div');
            viewport.className = 'gh-viewport';
            viewport.appendChild(mainContent.cloneNode(true));
            newPage.appendChild(viewport);
            
            newPage.dataset.url = url;

            // Update current page without collapsing it
            const currentPage = this.container.querySelector('.current-page');
            if (currentPage) {
                this.scrollPositions.set(currentPage.dataset.url, currentPage.scrollTop);
                currentPage.classList.remove('current-page', 'active');
                
                this.stack.push({
                    element: currentPage,
                    url: currentPage.dataset.url
                });
            }

            // Add and activate new page
            this.container.appendChild(newPage);
            newPage.classList.add('current-page', 'active');
            
            // Update: Normalize paths when saving stack parameters
            const stackUrls = [...this.stack.map(item => item.url), url];
            const stackPaths = stackUrls.map(stackUrl => {
                const urlObj = new URL(stackUrl);
                // Remove trailing slash before encoding
                return encodeURIComponent(urlObj.pathname.replace(/\/+$/, ''));
            });
            
            // Create new URL with stack parameter
            const newUrl = new URL(url);
            newUrl.searchParams.set('stack', stackPaths.join(','));
            
            requestAnimationFrame(() => {
                newPage.scrollIntoView({ behavior: 'smooth', inline: 'start' });
                const savedScroll = this.scrollPositions.get(url);
                if (savedScroll) {
                    newPage.scrollTop = savedScroll;
                }
                
                setTimeout(() => {
                    this.isNavigating = false;
                }, 300);
            });

            window.history.pushState({
                stack: this.stack.map(item => item.url),
                currentUrl: url
            }, '', newUrl.toString());
        } catch (error) {
            console.error('Navigation failed:', error);
            this.isNavigating = false;
        }
    }

    handleHistoryNavigation(state) {
        const currentStack = state.stack || [];
        const currentUrl = state.currentUrl;

        // Remove excess pages
        while (this.stack.length > currentStack.length) {
            const page = this.stack.pop();
            page.element.remove();
        }

        // Update current page
        const currentPage = this.container.querySelector('.current-page');
        if (currentPage && currentPage.dataset.url !== currentUrl) {
            currentPage.remove();
            this.navigateTo(currentUrl);
        }
    }

    // Add method to handle URL parameters on page load
    async handleInitialStack() {
        const url = new URL(window.location.href);
        const stackParam = url.searchParams.get('stack');
        
        if (stackParam) {
            // First, try to split the raw stack parameter
            const stackPaths = stackParam.split(',')
                .map(path => {
                    try {
                        // Keep decoding until there are no more encoded characters
                        let decodedPath = path;
                        while (decodedPath.includes('%')) {
                            const prevPath = decodedPath;
                            decodedPath = decodeURIComponent(decodedPath);
                            if (prevPath === decodedPath) break; // Break if no change
                        }
                        return decodedPath;
                    } catch (e) {
                        console.error('Error decoding path:', path, e);
                        return path;
                    }
                });
            
            console.log('Decoded stack paths:', stackPaths);

            // Clear existing content
            this.container.innerHTML = '';
            this.stack = [];

            // Hide container initially
            this.container.style.opacity = '0';
            this.container.style.transition = 'opacity 0.3s ease-in-out';

            // Load all pages first
            const loadedPages = [];
            for (const path of stackPaths) {
                const fullUrl = path.startsWith('http') ? path : `${window.location.origin}${path}`;
                const page = await this.loadStackedPage(fullUrl);
                if (page) {
                    page.style.opacity = '0';
                    page.style.transition = 'opacity 0.3s ease-in-out';
                    loadedPages.push(page);
                }
            }

            // Calculate final scroll position based on page width and count
            const singlePageWidth = loadedPages[0]?.offsetWidth || 0;
            const targetScrollPosition = singlePageWidth * (loadedPages.length - 1);

            // Once all pages are loaded, start the animation sequence
            requestAnimationFrame(() => {
                this.container.style.opacity = '1';
                
                if (targetScrollPosition > 0) {
                    this.container.scrollLeft = targetScrollPosition;
                }
                
                setTimeout(() => {
                    loadedPages.slice(0, -1).forEach(page => {
                        page.style.opacity = '1';
                    });

                    setTimeout(() => {
                        const lastPage = loadedPages[loadedPages.length - 1];
                        if (lastPage) {
                            lastPage.style.opacity = '1';
                        }
                    }, 150);
                }, 300);
            });
        }
    }

    async loadStackedPage(url) {
        try {
            const urlObj = new URL(url);
           
            
            const response = await fetch(url);
            const html = await response.text();
            
            // Create a temporary container to parse the HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Find the main content - adjust selector based on your template structure
            const mainContent = doc.querySelector('.site-main') || 
                              doc.querySelector('.gh-content') ||
                              doc.querySelector('.gh-viewport > *');
                              
            if (!mainContent) {
                console.error('Could not find main content in loaded page');
                return;
            }

            this.logPageContent(mainContent, 'loadStackedPage');

            const newPage = document.createElement('div');
            newPage.className = 'stacked-page';
            
            // Create viewport container for consistent layout
            const viewport = document.createElement('div');
            viewport.className = 'gh-viewport';
            viewport.appendChild(mainContent.cloneNode(true));
            newPage.appendChild(viewport);
            
            newPage.dataset.url = url;

            // Update existing page classes
            const currentPage = this.container.querySelector('.current-page');
            if (currentPage) {
                currentPage.classList.remove('current-page', 'active');
                this.stack.push({
                    element: currentPage,
                    url: currentPage.dataset.url
                });
            }

            // Add the new page
            this.container.appendChild(newPage);
            newPage.classList.add('current-page', 'active');
            
            // Update: Normalize paths when saving stack parameters
            const stackUrls = [...this.stack.map(item => item.url), url];
            const stackPaths = stackUrls.map(stackUrl => {
                const urlObj = new URL(stackUrl);
                // Remove trailing slash before encoding
                return encodeURIComponent(urlObj.pathname.replace(/\/+$/, ''));
            });
            
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('stack', stackPaths.join(','));
            window.history.replaceState(
                { stack: this.stack.map(item => item.url), currentUrl: url },
                '',
                newUrl.toString()
            );
            
            return newPage; // Return the page element for animation
        } catch (error) {
            console.error('Failed to load stacked page:', error);
            return null;
        }
    }

    logPageContent(content, source) {
        console.log(`Content from ${source}:`, {
            element: content,
            html: content.outerHTML,
            classes: content.className,
            children: content.children.length
        });
    }
}

// Initialize
new StackedNavigation();